import type { AiOrgAdviceRunSummary } from "@closer/shared";
import {
  AI_CERTAINTY_WARNING,
  AI_ORG_ADVICE_ADVISORY,
  AI_ORG_ADVICE_BLOCKED_WARNING,
  AI_ORG_ADVICE_CATEGORY_NAMES,
  AI_ORG_ADVICE_EMPTY_MESSAGE,
  AI_ORG_ADVICE_PRIORITY_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * ORGANİZASYON/ROL AI ÖNERİLERİ (CLOS-288 `[192]` · CLOS-1015).
 *
 * ### ⚠ "UYGULA" DÜĞMESİ YOK
 *
 * Kart: *"AI ... rol, permission, kullanıcı ataması ... oluşturmaz/değiştirmez"* ve
 * *"Kullanıcı öneriyi uygulamayı seçer ... ilgili kanonik command ... çalışır."* Bu bölüm
 * öneriyi ve ETKİSİNİ gösteriyor; değişikliği kullanıcı aynı sayfadaki rol düzenleme
 * akışında yapıyor. Bir "uygula" düğmesi koysaydık, yetki değişikliğini AI'ın önerisiyle
 * bir tık arasına indirmiş olurduk — kartın yasakladığı tam olarak bu.
 *
 * ### ⚠ UYGULANAMAZ ÖNERİ AÇIKÇA İŞARETLENİYOR
 *
 * Sunucu her önerilen izni kanonik kapıdan (`evaluateGrant`) geçiriyor. Geçemeyen anahtar
 * ekranda SEBEBİYLE görünüyor. Gizleseydik kullanıcı öneriyi uygulamayı deneyip rol
 * ekranında reddedilir ve nedenini orada arardı.
 */
export function AiOrgAdviceSection() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const anahtar = tenantKey(tenantId, "org-ai-onerileri");
  const liste = useQuery({
    queryKey: anahtar,
    enabled: can("organization.role"),
    queryFn: async () =>
      (await api<AiOrgAdviceRunSummary[]>(`/api/roles/ai-advice`, { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const uret = useMutation({
    mutationFn: async () =>
      (
        await api<AiOrgAdviceRunSummary>(`/api/roles/ai-advice`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async (sonuc) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      /**
       * ⚠ SONUÇ DOĞRUDAN ÖNBELLEĞE YAZILIYOR, yalnız invalidate EDİLMİYOR: üretim
       * saniyeler sürüyor ve kullanıcının o süre sonunda gördüğü şey bir liste
       * tazelemesinin yarışına bırakılmamalı. Ölçüldü: yalnız invalidate ile E2E'de
       * öneri kartı hiç görünmedi. Invalidate yine de kalıyor — sunucunun listesi tek
       * gerçek kaynak ve yeni çalışma orada da olmalı.
       */
      queryClient.setQueryData<AiOrgAdviceRunSummary[]>(anahtar, (eski) => [
        sonuc,
        ...(eski ?? []),
      ]);
      await queryClient.invalidateQueries({ queryKey: anahtar });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Öneriler üretilemedi"),
  });

  /** ⚠ Yetkisiz kullanıcıda bölüm HİÇ çizilmiyor (sunucu kapısıyla aynı anahtar). */
  if (!can("organization.role")) return null;

  const son = (liste.data ?? []).find((c) => c.status === "completed") ?? null;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">
          AI yetki düzeni önerileri
        </h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uret.isPending}
          disabled={uret.isPending}
          onClick={() => uret.mutate()}
        >
          {uret.isPending ? "İnceleniyor…" : son ? "Yeniden incele" : "Yetki düzenimi incele"}
        </Button>
      </div>

      <AiAdvisoryNotice />
      {/* ⚠ Kartın zorunlu cümlesi: hiçbir öneri kendiliğinden uygulanmaz. */}
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-org-uyari">
        {AI_ORG_ADVICE_ADVISORY}
      </p>

      <div className="mt-4">
        {liste.isPending ? (
          <Skeleton lines={3} />
        ) : liste.error ? (
          <StateView
            state={resolveErrorState(liste.error, {
              idempotent: true,
              overrides: {
                error: { title: "Öneriler yüklenemedi", description: "Bölüm getirilemedi." },
              },
            })}
            onRetry={() => void liste.refetch()}
          />
        ) : !son ? (
          <p className="text-sm text-fg-muted">
            Rol, üyelik ve departman düzeninizi AI ile inceleyip öneri alabilirsiniz.
          </p>
        ) : son.advices.length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="ai-org-bos">
            {AI_ORG_ADVICE_EMPTY_MESSAGE}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge tone="neutral">{son.model}</Badge>
              <span className="tabular-nums">{son.credits} kredi</span>
            </div>

            {son.advices.map((oneri, i) => (
              <article
                key={i}
                data-testid={`ai-org-oneri-${i}`}
                className="rounded-lg border border-border-subtle p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={oneri.priority === "high" ? "warning" : "neutral"}>
                    {AI_ORG_ADVICE_PRIORITY_NAMES[oneri.priority]}
                  </Badge>
                  <Badge tone="neutral">{AI_ORG_ADVICE_CATEGORY_NAMES[oneri.category]}</Badge>
                  <span className="text-xs text-fg-muted">{oneri.subject}</span>
                </div>

                <h3 className="mt-2 text-sm font-semibold text-fg-heading">{oneri.title}</h3>
                <p className="mt-1 text-sm text-fg">{oneri.rationale}</p>
                {/*
                  ⚠ KESİN İFADE İŞARETİ (CLOS-1087). Cümle silinmiyor ya da düzeltilmiyor —
                  yanına ne olduğu yazılıyor. Sessizce düzeltmek AI'ın ne dediğini
                  kullanıcıdan gizlemek olurdu; reddetmek kullanıcının kredisini bir
                  kelime seçimi yüzünden yakardı. Risk ailesindeki kararla aynı.
                */}
                {oneri.certaintyFlagged ? (
                  <p className="mt-1 text-xs text-warning" data-testid="ai-org-kesinlik">
                    {AI_CERTAINTY_WARNING}
                  </p>
                ) : null}

                {oneri.permissionChange ? (
                  <div className="mt-2 text-sm text-fg-muted">
                    {oneri.permissionChange.add.length > 0 ? (
                      <p>Eklenmesi önerilen: {oneri.permissionChange.add.join(", ")}</p>
                    ) : null}
                    {oneri.permissionChange.remove.length > 0 ? (
                      <p>Kaldırılması önerilen: {oneri.permissionChange.remove.join(", ")}</p>
                    ) : null}
                    {/* ⚠ ETKİ SAYIYLA: kimlerin etkilendiği ayrı bir yetki sorusu. */}
                    <p className="mt-1">
                      Etkilenen üye: {oneri.affectedMembers} · bekleyen davet:{" "}
                      {oneri.affectedInvitations}
                    </p>
                  </div>
                ) : null}

                {oneri.blockedPermissions.length > 0 ? (
                  <InfoBanner tone="warning" className="mt-2">
                    <div data-testid={`ai-org-engel-${i}`}>
                      <p className="font-medium">{AI_ORG_ADVICE_BLOCKED_WARNING}</p>
                      <ul className="mt-1 list-disc pl-5 text-sm">
                        {oneri.blockedPermissions.map((e, j) => (
                          <li key={j}>
                            {e.permission}: {e.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </InfoBanner>
                ) : null}
              </article>
            ))}

            {son.reviewNeeded.length > 0 ? (
              <InfoBanner tone="warning">
                <div data-testid="ai-org-belirsizlik">
                  <p className="font-medium">Kesin olmayan noktalar</p>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {son.reviewNeeded.map((satir, i) => (
                      <li key={i}>{satir}</li>
                    ))}
                  </ul>
                </div>
              </InfoBanner>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}
